<template>
  <div class="application-questions">
    <div v-if="isLoading">
      <v-skeleton-loader v-for="i in 10" :key="i" type="list-item" />
    </div>
    <div v-else>
      <div
        v-for="(questionWithAnswers, index) in questionsWithAnswers"
        :key="questionWithAnswers.id"
      >
        <div class="item mb-5">
          <div class="question d-flex align-center">
            <v-chip v-if="pointedList" class="mr-2">
              <span class="font-weight-bold"> {{ index + 1 }} </span>
            </v-chip>
            {{ questionWithAnswers.question }}
          </div>
          <div v-if="shouldDisplayQuestionOptions(questionWithAnswers)">
            <div
              class="answer"
              v-for="answer in getAnswers(questionWithAnswers)"
              :key="answer"
              v-html="answer"
            />
          </div>
          <div
            v-else-if="
              isAnswerFile(questionWithAnswers) &&
                size(getAttachments(questionWithAnswers))
            "
          >
            <FileUpload
              class="mt-5"
              :files="getAttachments(questionWithAnswers)"
              :fileTypes="['application/pdf']"
              disabled
              @show="onShow"
              customActions
            />
            <PDFViewFull :data="fileDialog" />
          </div>
          <div v-else class="answer yes">
            <div v-html="fomattedAnswer(questionWithAnswers)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { APPLICATION_NAMESPACE } from "@/store/modules/applications";
import {
  FETCH_CURRENT_APPLICATION_ANSWERS,
  FETCH_BOOKING_APPLICATION_QUESTIONS
} from "@/store/modules/applications/actions";
import { createNamespacedHelpers } from "vuex";
import {
  GET_CURRENT_APPLICATION_ANSWERS,
  GET_BOOKING_APPLICATION_QUESTIONS
} from "@/store/modules/applications/getters";
import FileUpload from "@/components/common/FileUpload";
import { includes, filter, map, get, first, size } from "lodash";
import { getToken } from "@/services/utils";
import PDFViewFull from "@/components/common/PDFViewFull";

const {
  mapActions: mapApplicationActions,
  mapGetters: mapApplicationGetters
} = createNamespacedHelpers(APPLICATION_NAMESPACE);

const TYPES_WITH_OPTIONS = ["radio", "checkbox", "select"];

export default {
  name: "ApplicationQuestions",
  props: {
    applicationId: {
      type: String,
      required: true
    },
    bookingId: {
      type: String,
      required: true
    },
    pointedList: Boolean
  },
  components: {
    FileUpload,
    PDFViewFull
  },
  data() {
    return {
      fileDialog: {
        title: "",
        dialog: false,
        src: ""
      },
      isLoading: true
    };
  },
  async created() {
    this.isLoading = true;
    await Promise.all([
      this.fetchCurrentApplicationAnswers(this.applicationId),
      this.fetchBookingApplicationQuestions(this.bookingId)
    ]);
    this.isLoading = false;
  },
  computed: {
    ...mapApplicationGetters({
      currentApplicationAnswers: GET_CURRENT_APPLICATION_ANSWERS,
      bookingApplicationQuestions: GET_BOOKING_APPLICATION_QUESTIONS
    }),
    questionsWithAnswers() {
      return map(this.bookingApplicationQuestions, question => {
        const filteredAnswers = first(
          filter(
            this.currentApplicationAnswers,
            answer => answer.question.id === question.id
          )
        );

        return {
          ...question,
          answersData: filteredAnswers
        };
      });
    }
  },
  methods: {
    ...mapApplicationActions({
      fetchCurrentApplicationAnswers: FETCH_CURRENT_APPLICATION_ANSWERS,
      fetchBookingApplicationQuestions: FETCH_BOOKING_APPLICATION_QUESTIONS
    }),
    shouldDisplayQuestionOptions(question) {
      return includes(TYPES_WITH_OPTIONS, question.inputType);
    },
    isAnswerFile(question) {
      return question.inputType === "file";
    },
    async onShow(file) {
      const token = getToken();
      this.fileDialog = {
        src: `${file.url}?token=${token}`,
        title: file.fileName,
        dialog: true
      };
    },
    getAttachments(questionWithAnswers) {
      return get(questionWithAnswers.answersData, "attachments") || [];
    },
    getAnswers(questionWithAnswers) {
      return questionWithAnswers.answersData
        ? questionWithAnswers.answersData.answers
        : [];
    },
    fomattedAnswer(questionWithAnswers) {
      return first(get(questionWithAnswers.answersData, "answers"));
    },
    first,
    get,
    size
  }
};
</script>

<style lang="scss">
.application-questions {
  margin-bottom: 20px;

  .question {
    font-weight: bold;
  }
  .answer {
    margin: 10px 0;
    @include body;
  }
}
</style>
